import React, { memo, useState, useEffect, useCallback } from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WorkIcon from '@mui/icons-material/Work';
import apiFetch from '../utils/apiFetch'; 

const JobSelection = memo(({ 
  isDisabled, 
  companyName, 
  setCompanyName, 
  jobId, 
  setJobId,
  setCandidatePreference,
  setJobTitle,
  setJobsDataProps,
  showAlert,
  applicantName,
  applicantMobile,
  userDevice,
  userPhone,
  onViewJob,
  children 
}) => {
  const [companies, setCompanies] = useState([]);
  const [vacancies, setVacancies] = useState([]);

  useEffect(() => {
    const apiURL = `${process.env.REACT_APP_API_URL}/jobs/companies`;
    
    apiFetch(apiURL, {
      method: "GET",
      headers: { authtoken: localStorage.getItem("loggedintoken") }
    })
    .then(res => res.status === 200 ? res.json() : res.text())
    .then(repos => {
      if (repos.data) {
        setCompanies(repos.data);
        if (!companyName && repos.data.length > 0) {
          setCompanyName(repos.data[0]);
        }
      } else if (showAlert) {
        showAlert("13 " + (repos.message || repos), "error");
      }
    })
    .catch(err => showAlert && showAlert(`77 ${err}`, "error"));
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  useEffect(() => {
    if (!companyName || !companyName.includes(" - ")) return;

    const [company, office] = companyName.split(" - ");
    const apiURL = `${process.env.REACT_APP_API_URL}/jobs/vacancies?company=${company}&office=${office}`;

    apiFetch(apiURL, {
      method: "GET",
      headers: { authtoken: localStorage.getItem("loggedintoken") }
    })
    .then(res => res.status === 200 ? res.json() : res.text())
    .then(repos => {
      if (repos.data) {
        setVacancies(repos.data);
        
        const jobExists = repos.data.find(v => v._id === jobId);
        if (!jobExists && repos.data.length > 0) {
          const firstJob = repos.data[0];
          setJobId(firstJob._id);
          setJobTitle(firstJob.jobtitle);
          setCandidatePreference(firstJob.preferences);
          if (setJobsDataProps) setJobsDataProps(firstJob);
        } else if (jobExists && setJobsDataProps) {
          setJobsDataProps(jobExists); 
        }
      }
    })
    .catch(err => showAlert && showAlert(`888 ${err}`, "error"));
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  const selectedVacancy = vacancies?.find(v => v._id === jobId);

  const handleCompanyChange = useCallback((e) => {
    setCompanyName(e.target.value); 
  }, [setCompanyName]);

  const handleJobChange = useCallback((e) => {
    const selectedJobId = e.target.value;
    setJobId(selectedJobId);
    
    const vacancy = vacancies?.find(v => v._id === selectedJobId);
    if (vacancy) {
      setCandidatePreference(vacancy.preferences);
      setJobTitle(vacancy.jobtitle);
      if (setJobsDataProps) setJobsDataProps(vacancy);
    }
  }, [setJobId, vacancies, setCandidatePreference, setJobTitle, setJobsDataProps]);

  const SendMapLocation = useCallback(() => {
    if (!applicantMobile) return showAlert && showAlert("Applicant mobile not found.", "error");
    
    const safeName = applicantName ? applicantName[0].toUpperCase() + applicantName.slice(1) : "Candidate";
    const phoneStr = applicantMobile.trim().replace(' ', '');
    const last10 = phoneStr.substring(phoneStr.length - 10);
    const activeMapUrl = selectedVacancy?.mapUrl || "";

    const apiURL = `https://${userDevice === "mobile" ? "api" : "web"}.whatsapp.com/send/?phone=+91${last10}&type=phone_number&app_absent=0&text=Good Day ${safeName},%0a%0aYour interview will be conducted at the below location. %0a%0a ${activeMapUrl} %0a%0aKindly carry your resume hard copy and mention "Bizi Bees" on top of your resume without fail.%0a%0aPlease call me back on ${userPhone} if any queries.%0a%0aFollow us on LinkedIn by clicking https://www.linkedin.com/company/bizi-bees-outsourcing`;
    window.open(apiURL, '_blank');
  }, [applicantMobile, applicantName, selectedVacancy, userDevice, userPhone, showAlert]);

  const SendJD = useCallback(() => {
    if (!applicantMobile) return showAlert && showAlert("Applicant mobile not found.", "error");

    const safeName = applicantName ? applicantName[0].toUpperCase() + applicantName.slice(1) : "Candidate";
    const phoneStr = applicantMobile.trim().replace(' ', '');
    const last10 = phoneStr.substring(phoneStr.length - 10);
    const jd = selectedVacancy?.jobdescription || "";
    const skills = selectedVacancy?.skills || "";

    const apiURL = `https://${userDevice === "mobile" ? "api" : "web"}.whatsapp.com/send/?phone=+91${last10}&type=phone_number&app_absent=0&text=Good Day ${safeName},%0a%0aBelow is the Job Description and Skill Sets Required for the job discussed. %0a%0aJob Description:%0a${jd.split('\n').join('%0a')}%0a%0aSkills Required:%0a${skills.split('\n').join('%0a')}%0a%0aPlease call me back on ${userPhone} if any queries.%0a%0aFollow us on LinkedIn by clicking https://www.linkedin.com/company/bizi-bees-outsourcing`;
    window.open(apiURL.replace('%0a,', '%0a'), '_blank');
  }, [applicantMobile, applicantName, selectedVacancy, userDevice, userPhone, showAlert]);

  return (
    <>
      <div className='Schedule_CompanyName'>
        <FormControl variant="outlined" sx={{ width: 250 }} size="small">
          <InputLabel id="companyName-label"><ApartmentIcon /> &nbsp; Company Name</InputLabel>
          <Select
            labelId="company-name-label"
            id="Schedule_CompanyName"
            disabled={isDisabled}
            value={companyName || ""}
            InputLabelProps={{ shrink: true }}
            onChange={handleCompanyChange} 
          >
            {companies && companies.map((company) => (
              <MenuItem key={company} value={company}>{company}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {children}

      <div className='Schedule_Available_Job'>
        <FormControl variant="outlined" sx={{ width: 250 }} size="small">
          <InputLabel id="vacancies-label"><WorkIcon /> &nbsp; Available Vacancies</InputLabel>
          <Select
            labelId="vacancies-label"
            id="Schedule_Available_Job"
            disabled={isDisabled}
            value={jobId || ""} 
            onChange={handleJobChange} 
          >
            {vacancies && vacancies.map((vacancy) => (
              <MenuItem key={vacancy._id} value={vacancy._id}>{vacancy.jobtitle}</MenuItem>
            ))}
          </Select>
          {selectedVacancy?.languages}
        </FormControl>
      </div>

      <div>
        <img id="_Office_location" src="location.png" srcSet="location.png 1x, location@2x.png 2x" alt="location icon" />
        <a href="#" id='Company_Send_location' onClick={(e) => { e.preventDefault(); SendMapLocation(); }}>Send Location</a>
      </div>
      <div>
        <img id="document" src="document.png" srcSet="document.png 1x, document@2x.png 2x" alt="document icon" />
        <a href="#" id="Sending_Company_JD" onClick={(e) => { e.preventDefault(); SendJD(); }}>Send JD</a>
      </div>
      <div>
        <img id="view" src="view.png" srcSet="view.png 1x, view@2x.png 2x" alt="view icon" />
        <a href="#" id="View_Job_bg" onClick={(e) => { e.preventDefault(); if(onViewJob) onViewJob(); }}>View</a>
      </div>
    </>
  );
});

export default JobSelection;